/**
 * Sharing (rule SH1, spec § 7).
 *
 * `shareText` is transcribed from the prototype line for line, because this is
 * what a buyer pastes into WhatsApp for a colleague — the field order and the
 * separators are what make it readable on a phone.
 *
 * Delivery order: native share sheet first, then clipboard. In a PWA the share
 * sheet works; in a desktop browser it usually does not, so the clipboard
 * fallback is the normal path there rather than an error case.
 */

import { money, paidAtBooth, priceLine, primaryContact } from '../rules';

export function shareText(supplier, fair) {
  const contact = primaryContact(supplier) || {};
  const paid = paidAtBooth(supplier);

  return [
    supplier.company +
      (supplier.booth ? ` — Booth ${supplier.booth}` : '') +
      (supplier.phase ? ` (Phase ${supplier.phase})` : ''),
    fair ? fair.name : '',
    ...(supplier.products || []).map(
      (p) =>
        `• ${p.name}: ${priceLine(p, fair) || 'no price'}` +
        (p.moq ? ` · MOQ ${p.moq}` : '') +
        ` · ${p.decision}` +
        (p.rating ? ` · ${'★'.repeat(p.rating)}` : '') +
        (p.notes ? ` — ${p.notes}` : ''),
    ),
    paid ? `Paid at booth: ${money(paid)}` : '',
    contact.name
      ? `Contact: ${contact.name}` +
        (contact.phone ? ` ${contact.phone}` : '') +
        (contact.wechat ? ` · WeChat ${contact.wechat}` : '') +
        (contact.email ? ` · ${contact.email}` : '')
      : '',
    supplier.chinaNotes ? `Notes: ${supplier.chinaNotes}` : '',
  ]
    .filter(Boolean)
    .join('\n');
}

/** Share a supplier summary. `toast` reports the outcome either way. */
export async function shareSupplierText(supplier, fair, toast) {
  const text = shareText(supplier, fair);

  try {
    if (navigator.share) {
      await navigator.share({ title: supplier.company, text });
      return;
    }
  } catch (e) {
    // A user dismissing the sheet is not a failure — say nothing.
    if (e && e.name === 'AbortError') return;
  }

  try {
    await navigator.clipboard.writeText(text);
    if (toast) toast('Summary copied — paste it in WhatsApp');
  } catch (e) {
    if (toast) toast('Could not share on this device');
  }
}

/** wa.me link carrying the summary, for a direct WhatsApp hand-off. */
export const whatsappHref = (supplier, fair) =>
  `https://wa.me/?text=${encodeURIComponent(shareText(supplier, fair))}`;
